<script>
import moment from "moment";

import Portal from "../../layouts/portal";
import { cargarVacantes, enlaceWhatsapp } from "@/services/portal";
import { parseFechaFlexible } from "@/services/usuarios";

/**
 * Bolsa de trabajo. Sin muro de pago, igual que en la app.
 */
export default {
  page: { title: "Vacantes" },
  components: { Portal },
  data() {
    return {
      vacantes: [],
      busqueda: "",
      cargando: true,
      error: null,
      seleccionada: null,
      mostrarDetalle: false,
    };
  },
  computed: {
    vacantesFiltradas() {
      const q = this.busqueda.trim().toLowerCase();
      if (!q) return this.vacantes;
      return this.vacantes.filter((v) =>
        [v.tituloPuesto, v.nombreEmpresa, v.departamento, v.tipoContrato]
          .filter(Boolean)
          .some((campo) => String(campo).toLowerCase().includes(q))
      );
    },
  },
  methods: {
    publicada(vacante) {
      const fecha = parseFechaFlexible(vacante.fechaPublicacion);
      return fecha ? moment(fecha).fromNow() : "";
    },
    contacto(vacante) {
      return enlaceWhatsapp(
        vacante.contacto,
        `Hola, me interesa la vacante de ${vacante.tituloPuesto} que vi en Recnet.`
      );
    },
    verDetalle(vacante) {
      this.seleccionada = vacante;
      this.mostrarDetalle = true;
    },
    async cargar() {
      this.cargando = true;
      this.error = null;
      try {
        this.vacantes = await cargarVacantes();
      } catch (err) {
        console.error("[portal] error al cargar vacantes:", err);
        this.error = "No pudimos cargar las vacantes. Inténtalo de nuevo en un momento.";
      } finally {
        this.cargando = false;
      }
    },
  },
  mounted() {
    this.cargar();
  },
};
</script>

<template>
  <Portal>
    <h1 class="h3 mb-1">Vacantes</h1>
    <p class="text-muted">Oportunidades publicadas por los negocios de la red.</p>

    <b-form-input v-model="busqueda" type="search" placeholder="Buscar puesto, empresa o departamento…"
      class="portal-buscador mb-4" />

    <b-alert :show="!!error" variant="warning">{{ error }}</b-alert>
    <p v-if="cargando" class="text-muted">Cargando vacantes…</p>
    <p v-else-if="vacantesFiltradas.length === 0" class="text-muted">
      Por ahora no hay vacantes publicadas.
    </p>

    <div v-else>
      <div v-for="vacante in vacantesFiltradas" :key="vacante.id" class="vacante-card"
        @click="verDetalle(vacante)">
        <div class="vacante-principal">
          <h3 class="vacante-puesto">{{ vacante.tituloPuesto }}</h3>
          <p class="vacante-empresa mb-1">{{ vacante.nombreEmpresa }}</p>
          <div class="vacante-etiquetas">
            <b-badge v-if="vacante.departamento" variant="light">{{ vacante.departamento }}</b-badge>
            <b-badge v-if="vacante.tipoContrato" variant="light">{{ vacante.tipoContrato }}</b-badge>
            <b-badge v-if="vacante.rangoSalarial" variant="light">{{ vacante.rangoSalarial }}</b-badge>
          </div>
        </div>
        <div class="vacante-meta">
          <span class="text-muted small">{{ publicada(vacante) }}</span>
          <i class="mdi mdi-chevron-right"></i>
        </div>
      </div>
    </div>

    <b-modal v-model="mostrarDetalle" size="lg" centered hide-footer
      :title="seleccionada ? seleccionada.tituloPuesto : ''">
      <div v-if="seleccionada">
        <p class="text-muted mb-3">
          {{ seleccionada.nombreEmpresa }}
          <span v-if="seleccionada.departamento"> · {{ seleccionada.departamento }}</span>
        </p>

        <ul class="list-unstyled mb-3">
          <li v-if="seleccionada.tipoContrato" class="mb-2">
            <strong>Tipo de contrato:</strong> {{ seleccionada.tipoContrato }}
          </li>
          <li v-if="seleccionada.rangoSalarial" class="mb-2">
            <strong>Rango salarial:</strong> {{ seleccionada.rangoSalarial }}
          </li>
        </ul>

        <template v-if="seleccionada.requisitosIndispensables">
          <h6>Requisitos indispensables</h6>
          <p class="vacante-texto">{{ seleccionada.requisitosIndispensables }}</p>
        </template>

        <template v-if="seleccionada.beneficios">
          <h6>Beneficios</h6>
          <p class="vacante-texto">{{ seleccionada.beneficios }}</p>
        </template>

        <div class="mt-4">
          <a v-if="contacto(seleccionada)" :href="contacto(seleccionada)" target="_blank"
            rel="noopener noreferrer" class="btn btn-success mr-2">
            <i class="mdi mdi-whatsapp"></i> Contactar
          </a>
          <a v-else-if="seleccionada.register_by" :href="`mailto:${seleccionada.register_by}`"
            class="btn btn-primary">
            <i class="mdi mdi-email-outline"></i> Enviar correo
          </a>
        </div>
      </div>
    </b-modal>
  </Portal>
</template>

<style scoped>
.portal-buscador {
  max-width: 360px;
}

.vacante-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: box-shadow 0.15s ease;
}

.vacante-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.vacante-puesto {
  font-size: 1.05rem;
  font-weight: 600;
  margin-bottom: 2px;
}

.vacante-empresa {
  color: #4b2e83;
  font-size: 0.9rem;
}

.vacante-etiquetas .badge {
  margin-right: 6px;
  font-weight: 500;
}

.vacante-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #74788d;
  flex-shrink: 0;
  margin-left: 16px;
}

.vacante-texto {
  white-space: pre-wrap;
  line-height: 1.6;
}
</style>
